import { generatePath } from "react-router";

import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";

import { getRelatedItemLink } from "utilities/relatedItem/relatedItem";
import { routes } from "utilities/routes";

import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { getRelatedItemEntityData } from "commonComponents/entity/relatedItem/RelatedItem.utilities";

/**
 * Minimal configuration mapping EntityType to RelatedItemType
 * Leverages existing utilities for labels, icons, and route generation
 * All entity routes are under the "/related-entities" prefix
 */
interface IEntityRouteConfig {
    /**
     * Route pattern for entity details (used by BTSelectEditable for route matching)
     * Example: "/Specification/:id/:jobId"
     */
    detailsPath: string;

    /**
     * Corresponding RelatedItemType enum value
     * Used to get labels, icons, and routes from existing utilities
     */
    relatedItemType: RelatedItemType;
}

/**
 * Global route prefix for all related entity routes
 * All entity detail modals will be under this prefix to prevent route conflicts
 */
export const RELATED_ENTITIES_PREFIX = "/related-entities";

/**
 * Registry mapping EntityType to route configuration
 * Add new entries here to enable entity linking for additional entity types
 */
export const ENTITY_LINK_ROUTE_REGISTRY: Partial<Record<EntityType, IEntityRouteConfig>> = {
    [EntityType.Specification]: {
        detailsPath: routes.specification.details,
        relatedItemType: RelatedItemType.Specification,
    },
    [EntityType.PlanSet]: {
        detailsPath: routes.plans.list,
        relatedItemType: RelatedItemType.PlanSet,
    },
    [EntityType.BidPackage]: {
        detailsPath: routes.bidPackage.details,
        relatedItemType: RelatedItemType.BidPackage,
    },
    // Future entities added here:
    // [EntityType.Rfi]: {
    //     detailsPathPattern: "/Rfi/:id/:jobId",
    //     relatedItemType: RelatedItemType.Rfi,
    // },
};

/**
 * Get route configuration for an entity type, or throw if not registered
 * @param entityType - The entity type to get configuration for
 * @returns The route configuration
 * @throws Error if entity type is not registered
 */
export function getEntityRouteConfig(entityType: EntityType): IEntityRouteConfig {
    const config = ENTITY_LINK_ROUTE_REGISTRY[entityType];
    if (!config) {
        throw new Error(
            `EntityType ${entityType} is not registered in ENTITY_LINK_ROUTE_REGISTRY. ` +
                `Please add configuration before using EntityLinkSelector with this entity type.`,
        );
    }
    return config;
}

/**
 * Get entity labels and icon from existing RelatedItem utilities
 * @param entityType - The entity type to get display data for
 * @returns Display data including name, namePlural, and icon component
 * @example
 * const data = getEntityDisplayData(EntityType.Specification);
 */
export function getEntityDisplayData(entityType: EntityType) {
    const config = getEntityRouteConfig(entityType);
    return getRelatedItemEntityData(config.relatedItemType);
}

/**
 * Get the detail path pattern for an entity type with RELATED_ENTITIES_PREFIX included
 * Used for route matching in BTSelectEditable
 * @param entityType - The entity type to get the path pattern for
 * @returns The detail path pattern with prefix (e.g., "/related-entities/Specification/:id/:jobId")
 */
export function getEntityDetailPathPattern(entityType: EntityType): string {
    const config = getEntityRouteConfig(entityType);
    return `${RELATED_ENTITIES_PREFIX}${config.detailsPath}`;
}

/**
 * Generate route to entity detail page using existing routing utilities
 * Handles both add (id=0) and edit (id>0) cases
 * Returns the full path with RELATED_ENTITIES_PREFIX included
 * @param entityType - The entity type to generate route for
 * @param entityId - The entity ID (0 for add, >0 for edit)
 * @param jobId - The job ID for job-scoped entities
 * @returns The route path with RELATED_ENTITIES_PREFIX prepended
 */
export function getEntityDetailRoute(
    entityType: EntityType,
    entityId: number,
    jobId: number,
): string {
    const config = getEntityRouteConfig(entityType);

    // For add (id=0), getRelatedItemLink returns empty string, so use generatePath instead
    if (entityId === 0) {
        return `${RELATED_ENTITIES_PREFIX}${generatePath(config.detailsPath, {
            id: entityId,
            jobId,
        })}`;
    }

    // For edit (id>0), use existing utility
    const link = getRelatedItemLink(entityId, config.relatedItemType, jobId);

    // getRelatedItemLink can return string or IRouteLocationState
    // Extract pathname if it's a location state, otherwise use as-is
    const pathname = typeof link === "string" ? link : link.pathname;
    return `${RELATED_ENTITIES_PREFIX}${pathname}`;
}
